import React, { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Loader2, Copy, Check, ExternalLink } from "lucide-react";
import workvivoSamlService from "../../services/workvivoSamlService";
import useCustomToast from "../../hooks/useCustomToast";
import { formatError } from "../../utils/errorHandler";
import { useTranslation } from "../../i18n";

/** Read-only URL row with a copy button — the SP values to paste into Workvivo. */
const CopyRow = ({ label, value }) => {
  const { t } = useTranslation("admin_integrations");
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — user can select manually */
    }
  };
  return (
    <div>
      <span className="text-xs font-medium text-slate-500">{label}</span>
      <div className="mt-1 flex items-center gap-2">
        <code className="flex-1 break-all rounded-lg bg-slate-100 px-2 py-1.5 text-xs text-slate-800">
          {value}
        </code>
        <button
          type="button"
          onClick={copy}
          className="flex-shrink-0 p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500"
          title={t("saml.copy_label", { label })}
          aria-label={t("saml.copy_label", { label })}
        >
          {copied ? (
            <Check className="w-4 h-4 text-green-600" />
          ) : (
            <Copy className="w-4 h-4" />
          )}
        </button>
      </div>
    </div>
  );
};

const WorkvivoSamlSsoCard = ({ orgId }) => {
  const toast = useCustomToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation("admin_integrations");
  const [enabled, setEnabled] = useState(false);
  const [metadataUrl, setMetadataUrl] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["workvivo-saml", "config", orgId || "self"],
    queryFn: () => workvivoSamlService.getConfig(orgId),
    retry: false,
  });

  // Seed the form from the server once; background refetches (window focus,
  // invalidations) must not wipe what the user is typing.
  const seededRef = useRef(false);
  useEffect(() => {
    if (data && !seededRef.current) {
      setEnabled(!!data.saml_sso_enabled);
      setMetadataUrl(data.saml_idp_metadata_url || "");
      seededRef.current = true;
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (payload) => workvivoSamlService.updateConfig(payload, orgId),
    onSuccess: (updated) => {
      setEnabled(!!updated.saml_sso_enabled);
      setMetadataUrl(updated.saml_idp_metadata_url || "");
      queryClient.invalidateQueries({ queryKey: ["workvivo-saml", "config"] });
      toast.success(t("saml.saved"));
    },
    onError: (err) => {
      toast.error(formatError(err));
      if (data) setEnabled(!!data.saml_sso_enabled); // revert toggle
    },
  });

  const save = (nextEnabled, nextUrl) => {
    saveMutation.mutate({
      saml_sso_enabled: nextEnabled,
      saml_idp_metadata_url: (nextUrl ?? metadataUrl).trim() || null,
    });
  };

  const handleToggle = () => {
    const next = !enabled;
    if (next && !metadataUrl.trim()) {
      toast.error(t("saml.metadata_url_required"));
      return;
    }
    setEnabled(next);
    save(next, metadataUrl);
  };

  // Sentences with an emphasised term: split around the placeholder so the
  // <strong> survives translation.
  const metadataHintParts = t("saml.metadata_url_hint").split("{{term}}");
  const nameIdHintParts = t("saml.name_id_hint").split("{{term}}");

  const header = (
    <div className="flex items-center gap-2 mb-1">
      <KeyRound className="w-5 h-5 text-indigo-600" />
      <h3 className="text-lg font-semibold text-gray-900">
        {t("saml.title")}
      </h3>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-500">
        <Loader2 className="w-5 h-5 mr-2 animate-spin" /> {t("saml.loading")}
      </div>
    );
  }

  // GET returns 400 when no Workvivo integration exists for the org.
  if (error) {
    const message = formatError(error);
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        {header}
        <p className="text-sm text-gray-500">
          {/organization/i.test(message)
            ? t("saml.connect_first")
            : message}
        </p>
      </div>
    );
  }

  if (data && !data.has_integration) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        {header}
        <p className="text-sm text-gray-500">
          {t("saml.connect_first_reuse")}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          {header}
          <p className="text-sm text-gray-600">
            {t("saml.description")}
          </p>
        </div>
        <button
          type="button"
          onClick={handleToggle}
          disabled={saveMutation.isPending}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
            enabled ? "bg-indigo-600" : "bg-gray-300"
          }`}
          aria-pressed={enabled}
          aria-label={t("saml.toggle_aria")}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
              enabled ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>

      {/* Step 1: paste the IdP metadata URL from Workvivo */}
      <div className="mt-5">
        <label className="block text-sm font-medium text-slate-700 mb-1">
          {t("saml.metadata_url_label")}
        </label>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="url"
            value={metadataUrl}
            onChange={(e) => setMetadataUrl(e.target.value)}
            placeholder="https://workvivo.com/identity/<tenant>/metadata"
            className="flex-1 px-3 py-2 border border-slate-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
          <button
            type="button"
            onClick={() => save(enabled, metadataUrl)}
            disabled={saveMutation.isPending}
            className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-500 text-white rounded-full hover:from-indigo-700 hover:to-purple-600 transition-all shadow-md text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saveMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> {t("common:actions.saving")}
              </>
            ) : (
              t("common:actions.save")
            )}
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          {metadataHintParts[0]}
          <strong>{t("saml.metadata_url_term")}</strong>
          {metadataHintParts[1]}
        </p>
      </div>

      {/* Step 2: give these SP URLs back to the Workvivo admin */}
      <div className="mt-5 rounded-lg border border-indigo-100 bg-indigo-50/60 p-4 space-y-3">
        <p className="text-sm font-medium text-indigo-900">
          {t("saml.sp_details_title")}
        </p>
        <CopyRow label={t("saml.sp_entity_id")} value={data.sp_entity_id} />
        <CopyRow label={t("saml.acs_url")} value={data.acs_url} />
        <div className="flex items-center gap-1 text-xs text-indigo-800">
          <ExternalLink className="w-3.5 h-3.5" />
          <span>
            {t("saml.sp_metadata_hint")}
          </span>
        </div>
        <CopyRow label={t("saml.sp_metadata_url")} value={data.sp_metadata_url} />
        <p className="text-xs text-indigo-800/80">
          {nameIdHintParts[0]}
          <strong>{t("saml.name_id_term")}</strong>
          {nameIdHintParts[1]}
        </p>
      </div>
    </div>
  );
};

export default WorkvivoSamlSsoCard;
